export interface VideoFrameStats {
  fps: number;
  frameCount: number;
}

export interface VideoMetadataProbeResult {
  fps?: number;
  frameCount?: number;
  sizeBytes?: number;
}

export interface EstimateFpsOptions {
  duration?: number;
  sampleDurationMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface VideoStatsSource {
  file?: File;
  url: string;
  duration?: number;
}

interface ParsedBox {
  type: string;
  payloadStart: number;
  end: number;
}

interface ParsedVideoTrack {
  stats: VideoFrameStats;
  enabled: boolean | null;
}

interface FrameCallbackMetadata {
  mediaTime: number;
}

type FrameCallback = (now: number, metadata: FrameCallbackMetadata) => void;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: FrameCallback) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

const STANDARD_FPS = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60] as const;
const FPS_SNAP_TOLERANCE = 0.5;
const MIN_PLAUSIBLE_PLAYBACK_FPS = 10;
const DEFAULT_SAMPLE_DURATION_MS = 700;
const DEFAULT_TIMEOUT_MS = 5_000;
const UINT32_SIZE = 4;
const MAX_MOOV_PAYLOAD_BYTES = 16 * 1024 * 1024;
const MAX_CONCURRENT_VIDEO_PROBES = 4;

type ProbeRelease = () => void;

interface ProbeWaiter {
  signal?: AbortSignal;
  cancelled: boolean;
  handleAbort: () => void;
  resolve: (release: ProbeRelease | null) => void;
}

let activeVideoProbes = 0;
const videoProbeWaiters: ProbeWaiter[] = [];

function makeProbeRelease(): ProbeRelease {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeVideoProbes -= 1;
    dispatchVideoProbeWaiters();
  };
}

function dispatchVideoProbeWaiters(): void {
  while (activeVideoProbes < MAX_CONCURRENT_VIDEO_PROBES && videoProbeWaiters.length > 0) {
    const waiter = videoProbeWaiters.shift()!;
    waiter.signal?.removeEventListener('abort', waiter.handleAbort);
    if (waiter.cancelled || waiter.signal?.aborted) continue;
    activeVideoProbes += 1;
    waiter.resolve(makeProbeRelease());
  }
}

function acquireVideoProbe(signal?: AbortSignal): Promise<ProbeRelease | null> {
  if (signal?.aborted) return Promise.resolve(null);
  if (activeVideoProbes < MAX_CONCURRENT_VIDEO_PROBES) {
    activeVideoProbes += 1;
    return Promise.resolve(makeProbeRelease());
  }

  return new Promise(resolve => {
    const waiter: ProbeWaiter = {
      signal,
      cancelled: false,
      handleAbort: () => {
        waiter.cancelled = true;
        waiter.resolve(null);
      },
      resolve,
    };
    signal?.addEventListener('abort', waiter.handleAbort, { once: true });
    videoProbeWaiters.push(waiter);
  });
}

function readType(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
}

function parseBox(view: DataView, offset: number, parentEnd: number): ParsedBox | null {
  if (offset < 0 || parentEnd - offset < 8) return null;

  const size32 = view.getUint32(offset);
  const type = readType(view, offset + UINT32_SIZE);
  let headerSize = 8;
  let size: number;

  if (size32 === 1) {
    if (parentEnd - offset < 16) return null;
    const largeSize = view.getBigUint64(offset + 8);
    if (largeSize > BigInt(Number.MAX_SAFE_INTEGER)) return null;
    size = Number(largeSize);
    headerSize = 16;
  } else if (size32 === 0) {
    size = parentEnd - offset;
  } else {
    size = size32;
  }

  if (size < headerSize || size > parentEnd - offset) return null;

  return {
    type,
    payloadStart: offset + headerSize,
    end: offset + size,
  };
}

function parseChildren(view: DataView, start: number, end: number): ParsedBox[] | null {
  const boxes: ParsedBox[] = [];
  let offset = start;

  while (offset < end) {
    const box = parseBox(view, offset, end);
    if (!box) return null;
    boxes.push(box);
    offset = box.end;
  }

  return boxes;
}

function findChild(boxes: ParsedBox[], type: string): ParsedBox | null {
  return boxes.find(box => box.type === type) ?? null;
}

function childBoxes(view: DataView, parent: ParsedBox): ParsedBox[] | null {
  return parseChildren(view, parent.payloadStart, parent.end);
}

function readHandlerType(view: DataView, hdlr: ParsedBox): string | null {
  // Full-box header (version + flags), pre_defined, then handler_type.
  if (hdlr.end - hdlr.payloadStart < 12) return null;
  return readType(view, hdlr.payloadStart + 8);
}

function readMdhdTimescale(view: DataView, mdhd: ParsedBox): number | null {
  if (mdhd.end - mdhd.payloadStart < 4) return null;
  const version = view.getUint8(mdhd.payloadStart);
  const timescaleOffset = version === 0
    ? mdhd.payloadStart + 12
    : version === 1
      ? mdhd.payloadStart + 20
      : -1;

  if (timescaleOffset < 0 || timescaleOffset + UINT32_SIZE > mdhd.end) return null;
  const timescale = view.getUint32(timescaleOffset);
  return timescale > 0 ? timescale : null;
}

function readSttsStats(view: DataView, stts: ParsedBox, timescale: number): VideoFrameStats | null {
  if (stts.end - stts.payloadStart < 8) return null;
  const entryCount = view.getUint32(stts.payloadStart + 4);
  const entriesStart = stts.payloadStart + 8;
  if (entryCount > Math.floor((stts.end - entriesStart) / 8)) return null;

  let frameCount = 0n;
  let totalDelta = 0n;

  for (let index = 0; index < entryCount; index += 1) {
    const entryOffset = entriesStart + index * 8;
    const sampleCount = BigInt(view.getUint32(entryOffset));
    const sampleDelta = BigInt(view.getUint32(entryOffset + UINT32_SIZE));
    frameCount += sampleCount;
    totalDelta += sampleCount * sampleDelta;
  }

  if (
    frameCount <= 0n
    || totalDelta <= 0n
    || frameCount > BigInt(Number.MAX_SAFE_INTEGER)
    || totalDelta > BigInt(Number.MAX_SAFE_INTEGER)
  ) return null;

  const numericFrameCount = Number(frameCount);
  const fps = numericFrameCount * timescale / Number(totalDelta);
  if (!Number.isFinite(fps) || fps <= 0) return null;

  return { fps, frameCount: numericFrameCount };
}

function readTrackEnabled(view: DataView, trakChildren: ParsedBox[]): boolean | null {
  const tkhd = findChild(trakChildren, 'tkhd');
  if (!tkhd || tkhd.end - tkhd.payloadStart < 4) return null;
  // tkhd is a full box: one version byte followed by three flag bytes. Bit 0
  // identifies tracks enabled for presentation.
  return (view.getUint8(tkhd.payloadStart + 3) & 1) === 1;
}

function parseVideoTrack(view: DataView, trak: ParsedBox): ParsedVideoTrack | null {
  const trakChildren = childBoxes(view, trak);
  const mdia = trakChildren && findChild(trakChildren, 'mdia');
  if (!mdia) return null;

  const mdiaChildren = childBoxes(view, mdia);
  if (!mdiaChildren) return null;

  const hdlr = findChild(mdiaChildren, 'hdlr');
  if (!hdlr || readHandlerType(view, hdlr) !== 'vide') return null;

  const mdhd = findChild(mdiaChildren, 'mdhd');
  const timescale = mdhd && readMdhdTimescale(view, mdhd);
  if (!timescale) return null;

  const minf = findChild(mdiaChildren, 'minf');
  const minfChildren = minf && childBoxes(view, minf);
  const stbl = minfChildren && findChild(minfChildren, 'stbl');
  const stblChildren = stbl && childBoxes(view, stbl);
  const stts = stblChildren && findChild(stblChildren, 'stts');
  const stats = stts ? readSttsStats(view, stts, timescale) : null;
  return stats ? { stats, enabled: readTrackEnabled(view, trakChildren) } : null;
}

function parseMoovPayload(buffer: ArrayBuffer): VideoFrameStats | null {
  const view = new DataView(buffer);
  const children = parseChildren(view, 0, view.byteLength);
  if (!children) return null;

  const tracks = children
    .filter(box => box.type === 'trak')
    .map(trak => parseVideoTrack(view, trak))
    .filter((track): track is ParsedVideoTrack => track !== null);

  return (
    tracks.find(track => track.enabled === true)
    ?? tracks.find(track => track.enabled !== false)
    ?? tracks[0]
  )?.stats ?? null;
}

async function readTopLevelBoxHeader(
  blob: Blob,
  offset: number,
  signal?: AbortSignal,
): Promise<ParsedBox | null> {
  if (signal?.aborted) return null;
  const shortHeader = await blob.slice(offset, offset + 8).arrayBuffer();
  if (signal?.aborted) return null;
  if (shortHeader.byteLength !== 8) return null;

  const shortView = new DataView(shortHeader);
  const size32 = shortView.getUint32(0);
  if (size32 !== 1) {
    const type = readType(shortView, UINT32_SIZE);
    const size = size32 === 0 ? blob.size - offset : size32;
    if (size < 8 || size > blob.size - offset) return null;
    return { type, payloadStart: offset + 8, end: offset + size };
  }

  const largeHeader = await blob.slice(offset, offset + 16).arrayBuffer();
  if (signal?.aborted) return null;
  if (largeHeader.byteLength !== 16) return null;
  const relativeBox = parseBox(new DataView(largeHeader), 0, blob.size - offset);
  return relativeBox
    ? {
        ...relativeBox,
        payloadStart: relativeBox.payloadStart + offset,
        end: relativeBox.end + offset,
      }
    : null;
}

/**
 * Reads an MP4/MOV's timing table without buffering its media payload.
 * Invalid and unsupported structures deliberately return null so probing never
 * blocks the generation flow.
 */
export async function parseMp4FrameStats(
  blob: Blob,
  signal?: AbortSignal,
): Promise<VideoFrameStats | null> {
  try {
    let offset = 0;

    while (offset < blob.size) {
      if (signal?.aborted) return null;
      const box = await readTopLevelBoxHeader(blob, offset, signal);
      if (!box) return null;

      if (box.type === 'moov') {
        const payloadSize = box.end - box.payloadStart;
        if (payloadSize > MAX_MOOV_PAYLOAD_BYTES) return null;
        const payload = await blob.slice(box.payloadStart, box.end).arrayBuffer();
        if (signal?.aborted || payload.byteLength !== payloadSize) return null;
        return parseMoovPayload(payload);
      }

      offset = box.end;
    }
  } catch {
    // Blob reads and malformed DataView access are supplementary and fail-open.
  }

  return null;
}

export function snapToStandardFps(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0;

  let nearest: number = STANDARD_FPS[0];
  for (const standard of STANDARD_FPS) {
    if (Math.abs(raw - standard) < Math.abs(raw - nearest)) nearest = standard;
  }

  return Math.abs(raw - nearest) <= FPS_SNAP_TOLERANCE ? nearest : Math.round(raw);
}

function positiveDuration(primary: number | undefined, fallback: number): number | null {
  if (typeof primary === 'number' && Number.isFinite(primary) && primary > 0) return primary;
  return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

export function estimateFpsByPlayback(
  url: string,
  options: EstimateFpsOptions = {},
): Promise<VideoFrameStats | null> {
  if (!url || typeof document === 'undefined') return Promise.resolve(null);

  const video = document.createElement('video') as VideoWithFrameCallback;
  if (typeof video.requestVideoFrameCallback !== 'function') {
    video.remove();
    return Promise.resolve(null);
  }

  const sampleDurationMs = Number.isFinite(options.sampleDurationMs) && options.sampleDurationMs! > 0
    ? options.sampleDurationMs!
    : DEFAULT_SAMPLE_DURATION_MS;
  const timeoutMs = Number.isFinite(options.timeoutMs) && options.timeoutMs! > 0
    ? options.timeoutMs!
    : DEFAULT_TIMEOUT_MS;

  return new Promise(resolve => {
    let settled = false;
    let callbackHandle: number | null = null;
    let duration: number | null = null;
    const mediaTimes: number[] = [];

    const cleanup = () => {
      clearTimeout(timeoutHandle);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
      video.removeEventListener('ended', handleEnded);
      options.signal?.removeEventListener('abort', handleAbort);
      try {
        if (callbackHandle !== null) video.cancelVideoFrameCallback?.(callbackHandle);
      } catch {
        // The browser may already have discarded the callback during teardown.
      }
      try {
        video.pause();
      } catch {
        // Playback cleanup is best-effort, but the element is always detached.
      }
      try {
        video.removeAttribute('src');
        video.load();
      } catch {
        // Some test/embedded media implementations do not support load().
      }
      try {
        video.remove();
      } catch {
        // A detached element has no further resources owned by this helper.
      }
    };

    const finish = (result: VideoFrameStats | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const finishFromSamples = () => {
      if (!duration || mediaTimes.length < 2) {
        finish(null);
        return;
      }

      const deltas: number[] = [];
      for (let index = 1; index < mediaTimes.length; index += 1) {
        const delta = mediaTimes[index] - mediaTimes[index - 1];
        if (Number.isFinite(delta) && delta > 0) deltas.push(delta);
      }

      const typicalDelta = median(deltas);
      const fps = typicalDelta ? snapToStandardFps(1 / typicalDelta) : 0;
      // rVFC callbacks can skip presented frames when the tab or detached media
      // element is throttled. A very low apparent rate is therefore less safe
      // than omitting the optional metadata and letting Dify probe the source.
      if (fps < MIN_PLAUSIBLE_PLAYBACK_FPS) {
        finish(null);
        return;
      }

      const frameCount = Math.round(duration * fps);
      finish(frameCount > 0 ? { fps, frameCount } : null);
    };

    const handleFrame: FrameCallback = (_now, metadata) => {
      if (settled) return;
      if (Number.isFinite(metadata.mediaTime) && metadata.mediaTime >= 0) {
        mediaTimes.push(metadata.mediaTime);
      }

      const first = mediaTimes[0];
      const last = mediaTimes[mediaTimes.length - 1];
      if (mediaTimes.length >= 2 && (last - first) * 1_000 >= sampleDurationMs) {
        finishFromSamples();
        return;
      }

      callbackHandle = video.requestVideoFrameCallback!(handleFrame);
    };

    function handleLoadedMetadata() {
      duration = positiveDuration(options.duration, video.duration);
      if (!duration) {
        finish(null);
        return;
      }

      callbackHandle = video.requestVideoFrameCallback!(handleFrame);
      Promise.resolve(video.play()).catch(() => finish(null));
    }

    function handleError() {
      finish(null);
    }

    function handleEnded() {
      finishFromSamples();
    }

    function handleAbort() {
      finish(null);
    }

    const timeoutHandle = setTimeout(() => finish(null), timeoutMs);
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);
    video.addEventListener('ended', handleEnded);
    options.signal?.addEventListener('abort', handleAbort, { once: true });

    if (options.signal?.aborted) {
      finish(null);
      return;
    }

    video.src = url;
    video.load();
  });
}

export async function getVideoStats(
  source: VideoStatsSource,
  signal?: AbortSignal,
): Promise<VideoFrameStats | null> {
  if (signal?.aborted) return null;

  if (source.file) {
    const parsed = await parseMp4FrameStats(source.file, signal);
    if (signal?.aborted) return null;
    if (parsed) return parsed;
  }

  return estimateFpsByPlayback(source.url, { duration: source.duration, signal });
}

export async function resolveVideoSizeBytes(
  source: Pick<VideoStatsSource, 'file' | 'url'>,
  signal?: AbortSignal,
): Promise<number | null> {
  if (source.file) return source.file.size;
  if (!source.url) return null;

  const controller = new AbortController();
  const handleAbort = () => controller.abort();
  const timeoutHandle = setTimeout(handleAbort, DEFAULT_TIMEOUT_MS);
  signal?.addEventListener('abort', handleAbort, { once: true });
  if (signal?.aborted) controller.abort();

  try {
    const response = await fetch(source.url, { method: 'HEAD', signal: controller.signal });
    if (!response.ok) return null;
    const value = response.headers.get('content-length');
    if (!value || !/^\d+$/u.test(value)) return null;
    const size = Number(value);
    return Number.isSafeInteger(size) && size >= 0 ? size : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutHandle);
    signal?.removeEventListener('abort', handleAbort);
  }
}

/**
 * Runs the complete optional metadata probe behind a shared browser-wide
 * concurrency limit. Slot configurations are intentionally unbounded, so this
 * prevents a large selection from starting dozens of playback streams and HEAD
 * requests at once. Every failure remains fail-open.
 */
export async function probeVideoMetadata(
  source: VideoStatsSource,
  signal?: AbortSignal,
): Promise<VideoMetadataProbeResult | null> {
  const release = await acquireVideoProbe(signal);
  if (!release) return null;

  try {
    const [stats, sizeBytes] = await Promise.all([
      getVideoStats(source, signal),
      resolveVideoSizeBytes(source, signal),
    ]);
    if (signal?.aborted) return null;

    const result: VideoMetadataProbeResult = {};
    if (stats && Number.isFinite(stats.fps) && stats.fps > 0) result.fps = stats.fps;
    if (stats && Number.isFinite(stats.frameCount) && stats.frameCount > 0) {
      result.frameCount = stats.frameCount;
    }
    if (sizeBytes !== null && Number.isFinite(sizeBytes) && sizeBytes > 0) {
      result.sizeBytes = sizeBytes;
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  } finally {
    release();
  }
}
